<template>
    <div class="row left-nav-collapsible">
        <div v-if="detailPageOpen" id="detail-page-open">
            <button class="btn details-open-btn" type="button" @click="onDetailPageOpenClose(false)">
                <span> <i class="fa fa-caret-right"></i></span>
            </button>
        </div>
        <TransitionGroup :name="jobs ? 'slide' : ''" key="detail">
            <div v-if="showCloseBtn && !detailPageOpen" id="detail-page-close" key="close">
                <button class="btn text-start details-open-btn" type="button" @click="onDetailPageOpenClose(true)">
                    <span> <i class="fa fa-caret-left"></i></span>
                </button>
            </div>
            <div v-if="!detailPageOpen" id="details-col" @mouseover="onDetailPageHover(true)" key="details-col">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-header-title">Deployment Details</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="loading">
                            <div class="d-flex justify-content-center align-items-center">
                                <div class="spinner-border spinner-border-sm me-2" role="status"
                                    style="width: 1em; height: 1em;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p style="padding-top: 15px;">Loading deployment details...</p>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="deployment">
                                <div class="list-group list-group-flush" style="font-size: 0.85rem;">
                                    <div class="list-group-item" v-if="deployment?.id">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployment ID</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ deployment.id }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.sandbox">
                                        <div class="row">
                                            <div class="col">
                                                <span>Sandbox</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <span>{{ deployment.sandbox }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.job_stack">
                                        <div class="row">
                                            <div class="col">
                                                <span>Job Stack</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <span>{{ deployment.job_stack }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.deployed_by">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployed By</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ deployment.deployed_by }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="deployment?.deployed_at">
                                        <div class="row">
                                            <div class="col">
                                                <span>Deployed At</span>
                                            </div>
                                            <div class="col-auto text-muted">
                                                <strong>{{ DateTimeFormat(deployment.deployed_at) }}</strong>
                                                <span class="ms-2 badge badge-secondary">{{
                                                    convertToRelativeTime(deployment.deployed_at) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center">
                                <div class="text-center py-3 bg-light rounded border border-dashed">
                                    <span class="text-muted">
                                        <i class="fa fa-info-circle"></i>
                                        No data found!
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="$store.state.currentUser.email.indexOf('@aqfer.com') !== -1" class="card">
                    <div class="card-body">
                        <h4 class="card-header-title mb-3">
                            Destination Sandbox
                            <span class="fa fa-circle-info text-muted" style="margin-left: 1px;"
                                :data-bs-title="'Select the destination sandbox for the pipeline jobs to promote'"
                                data-bs-toggle="tooltip" data-bs-placement="right">
                            </span>
                        </h4>
                        <div class="d-flex align-items-center justify-content-between gap-3">
                            <input-field v-model="destSandbox" :placeholder="'idr-edge-extract'"
                                :label="'Destination Sandbox'" :showLabel="false" :type="'select'"
                                :options="sandboxOptions" @change="$emit('destinationSandboxChange', destSandbox)" />
                            <button class="btn btn-primary" @click="$emit('onPromoteClick')" :disabled="!isDestinationSandboxAvailable || promoting != ''"
                                :data-bs-title="'Promote jobs'" data-bs-toggle="tooltip" data-bs-placement="right">
                                <span v-if="promoting" class="spinner-border spinner-border-sm" role="status" style="width: 1em; height: 1em;">
                                    <span class="visually-hidden">Loading...</span>
                                </span>
                                <i v-else class="fa fa-upload"></i>
                            </button>
                        </div>
                        <span v-if="destinationSandbox && !isDestinationSandboxAvailable" class="text-danger d-block mt-2" style="font-size: 0.75rem; line-height: 1.2">
                            <i class="fa fa-circle-exclamation me-1"></i>
                            <span>
                                The selected destination sandbox <strong>'{{ destinationSandbox }}'</strong> is not
                                available
                            </span>
                        </span>
                    </div>
                </div>
            </div>
            <div class="col" id="main-col" @mouseover="onDetailPageHover(false)" key="main-col">
                <div class="card">
                    <div class="card-header d-flex align-items-center justify-content-between">
                        <h4 class="card-header-title">
                            Pipeline Flow
                            <button type="button" v-if="pipelineJobConfig?.name" title="Click to view pipeline details"
                                class="btn btn-sm btn-outline-primary border-0 rounded-circle"
                                @click.prevent="redirectToSanboxJob(pipelineJobConfig)">
                                <i class="fa-solid fa-arrow-up-right-from-square small"></i>
                            </button>
                        </h4>
                        <div class="d-flex align-items-center justify-content-end">
                            <div>
                                <div class="mt-1"
                                    v-if="pipelineExecutionDetails?.status == 'RUNNING' || pipelineExecutionDetails?.status == 'SUBMITTED'">
                                    <span class="spinner-border spinner-border-sm me-1" role="status"
                                        style="width: 1em; height: 1em;">
                                        <span class="visually-hidden">Loading...</span>
                                    </span>
                                </div>
                            </div>
                            <span class="badge bg-secondary steps-badge"
                                v-if="pipelineExecutionDetails?.total && pipelineExecutionDetails?.completed">
                                {{ pipelineExecutionDetails?.completed }}/{{ pipelineExecutionDetails?.total }}
                                Jobs
                            </span>
                        </div>
                    </div>
                    <div class="card-body">
                        <div v-if="loading">
                            <div class="d-flex justify-content-center align-items-center">
                                <div class="spinner-border spinner-border-sm me-2" role="status"
                                    style="width: 1em; height: 1em;">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p style="padding-top: 15px;">Loading pipeline details...</p>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="Object.keys(jobs).length > 0 && pipelineJobConfig" style="height: 600px;">
                                <pipelineGraph :pipelineExecutionDetails="pipelineExecutionDetails"
                                    :key="`pipeline-${detailPageOpen}-${resetViewTrigger}`" :showresetViewBtn="true"
                                    :pipelineJobConfig="pipelineJobConfig" :jobs="jobs" @nodeClick="redirectToSanboxJob"
                                    @resetView="resetViewTrigger++">
                                </pipelineGraph>
                            </div>
                            <div v-else class="text-center">
                                No data found!
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </TransitionGroup>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { DateTimeFormat, relativeTime } from '../../utils/commonFunction';
import pipelineGraph from "./pipelineGraph.vue";
import InputField from '../../components/InputField.vue';
export default {
    name: 'JobStackDeploymentDetailsPipeline',
    components: { pipelineGraph, InputField },
    props: ["loading", "deployment", "jobs", "pipelineExecutionDetails", "destinationSandbox", "promoting"],
    emits: ['destinationSandboxChange', 'setRunAllButton'],
    data() {
        return {
            showCloseBtn: false,
            detailPageOpen: false,
            resetViewTrigger: 0,
            destSandbox: '',
        };
    },
    methods: {
        DateTimeFormat,
        convertToRelativeTime(timestamp) {
            return relativeTime(Date.now(), dayjs(timestamp));
        },
        redirectToSanboxJob(job) {
            this.$router.push({
                name: "Sandbox Job Configuration",
                params: {
                    sandboxId: `${this.deployment.sandbox}`,
                    jobId: `${job.name}`
                },
            });
        },
        onDetailPageOpenClose(value) {
            this.showCloseBtn = false;
            this.detailPageOpen = value;
        },
        onDetailPageHover(value) {
            this.showCloseBtn = value;
        },
    },
    computed: {
        pipelineJobConfig() {
            return Object.values(this.jobs).find(job => job.kind?.toLowerCase() === 'pipeline');
        },
        sandboxOptions() {
            return this.$store.state.sandbox.prodV2Sandboxes.map(sandbox => ({
                label: sandbox.name,
                value: sandbox.name,
            }));
        },
        isDestinationSandboxAvailable() {
            return this.$store.state.sandbox.prodV2Sandboxes.some(sandbox => sandbox.name === this.destinationSandbox);
        }
    },
    beforeMount() {
        this.destSandbox = this.destinationSandbox;
        this.$emit('setRunAllButton', "pipeline");
    },
    beforeUnmount() {
        this.$emit('setRunAllButton', "");
    },
}
</script>

<style scoped>
.steps-badge {
    font-size: 0.75rem;
    padding-left: 0.5rem;
}

::v-deep(.form-group) {
    margin-bottom: 0 !important;
}
</style>